import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { API_BASE_URL } from '../../lib/config';

interface BoldPicksAnalysisProps {
  season: number;
}

interface BoldPick {
  game_id: number;
  week: number;
  home_team: string;
  away_team: string;
  home_team_abbr: string;
  away_team_abbr: string;
  user_pick: string;
  majority_pick: string;
  result: string;
}

interface BoldRecord {
  wins: number;
  losses: number;
  pushes: number;
  total: number;
}

interface UserBoldPicks {
  user_id: string;
  display_name: string;
  bold_record: BoldRecord;
  bold_picks: BoldPick[];
}

const BoldPicksAnalysis: React.FC<BoldPicksAnalysisProps> = ({ season }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState<UserBoldPicks[]>([]);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError('');

      const response = await fetch(`${API_BASE_URL}/.netlify/functions/analysis/bold-picks/${season}`);

      if (!response.ok) {
        throw new Error('Failed to fetch bold picks data');
      }

      const result = await response.json();
      setData(result.users);
    } catch (err: any) {
      setError(err.message || 'Failed to load bold picks data');
    } finally {
      setLoading(false);
    }
  };

  const getResultChip = (result: string) => {
    const colorMap: { [key: string]: 'success' | 'error' | 'default' } = {
      win: 'success',
      loss: 'error',
      push: 'default'
    };
    return (
      <Chip
        label={result.toUpperCase()}
        color={colorMap[result] || 'default'}
        size="small"
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (data.length === 0) {
    return (
      <Box p={3}>
        <Alert severity="info">No bold picks data available for this season yet.</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h6" gutterBottom>
        Bold Picks Analysis
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        When a player goes against the grain (1 vs 3), how often does it pay off?
      </Typography>

      {data
        .sort((a: UserBoldPicks, b: UserBoldPicks) => b.bold_record.total - a.bold_record.total)
        .map((user: UserBoldPicks) => {
          const record = user.bold_record;
          const winRate = record.total > 0 ? ((record.wins / record.total) * 100).toFixed(1) : '0.0';

          return (
            <Paper key={user.user_id} elevation={3} sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                {user.display_name}'s Bold Picks ({record.total} picks)
              </Typography>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                <Box sx={{ flex: '1 1 120px', textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main">
                    {record.wins}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Wins
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 120px', textAlign: 'center' }}>
                  <Typography variant="h4" color="error.main">
                    {record.losses}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Losses
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 120px', textAlign: 'center' }}>
                  <Typography variant="h4" color="text.secondary">
                    {record.pushes}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Pushes
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 120px', textAlign: 'center' }}>
                  <Typography variant="h4" color="primary.main">
                    {winRate}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Win Rate
                  </Typography>
                </Box>
              </Box>

              {user.bold_picks.length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography>View All Bold Picks ({user.bold_picks.length})</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Week</TableCell>
                            <TableCell>Matchup</TableCell>
                            <TableCell>Bold Pick</TableCell>
                            <TableCell>Majority Pick</TableCell>
                            <TableCell>Result</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {user.bold_picks
                            .sort((a: BoldPick, b: BoldPick) => a.week - b.week)
                            .map((pick: BoldPick, index: number) => (
                              <TableRow key={index}>
                                <TableCell>{pick.week}</TableCell>
                                <TableCell>
                                  {pick.away_team_abbr} @ {pick.home_team_abbr}
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={pick.user_pick === 'home' ? pick.home_team_abbr : pick.away_team_abbr}
                                    color="secondary"
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Chip
                                    label={pick.majority_pick === 'home' ? pick.home_team_abbr : pick.away_team_abbr}
                                    color="default"
                                    size="small"
                                    variant="outlined"
                                  />
                                </TableCell>
                                <TableCell>{getResultChip(pick.result)}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </AccordionDetails>
                </Accordion>
              )}
            </Paper>
          );
        })}
    </Box>
  );
};

export default BoldPicksAnalysis;
